import {
  CapacityBar,
  CapacityBarSegment,
  CapacityBarLegend,
} from "~/react/ui/capacity-bar"

type EmailCapacityBreakdownProps = {
  current?: number
  total?: number
  secondary?: number
  label?: string
  showSecondary?: boolean
  chartSize?: "sm" | "md" | "lg"
  showLegend?: boolean
}

export function EmailCapacityBreakdown({
  current: currentProp,
  total: totalProp,
  secondary: secondaryProp,
  label,
  showSecondary: showSecondaryProp,
  chartSize = "sm",
  showLegend = false,
}: EmailCapacityBreakdownProps) {
  const current = currentProp ?? 0
  const total = totalProp ?? 0
  const secondary = secondaryProp ?? 0
  const showSecondary = showSecondaryProp ?? false

  const currentPercentage =
    total > 0 ? Math.min(100, (current / total) * 100) : 0
  const secondaryPercentage =
    total > 0 ? Math.min(100, (secondary / total) * 100) : 0
  const available = total - current - secondary

  const isFull = current >= total && secondary === 0

  const availablePercentage =
    showSecondary && !isFull
      ? Math.max(
          0,
          Math.min(100, 100 - currentPercentage - secondaryPercentage),
        )
      : 0

  const effectiveCurrentPercentage = isFull ? 100 : currentPercentage
  const shouldShowAvailable =
    showSecondary && available > 0 && availablePercentage > 0 && !isFull

  return (
    <>
      {label && <span className="mr-1">{label}</span>}
      <span>
        {current}/{total}
      </span>
      <div className="ml-1">
        <CapacityBar size={chartSize} primaryOnly={!showSecondary || isFull}>
          {current > 0 && (
            <CapacityBarSegment
              variant="current"
              percent={effectiveCurrentPercentage}
            />
          )}
          {secondary > 0 && showSecondary && !isFull && (
            <CapacityBarSegment
              variant="secondary"
              percent={secondaryPercentage}
            />
          )}
          {shouldShowAvailable && (
            <CapacityBarSegment
              variant="available"
              percent={availablePercentage}
            />
          )}
        </CapacityBar>
      </div>
      {showLegend && showSecondary && (
        <CapacityBarLegend
          items={[
            {
              variant: "current",
              label:
                label ||
                i18n.t("js.email_accounts_center.capacity.legend.current"),
              value: current,
            },
            {
              variant: "secondary",
              label: i18n.t(
                "js.email_accounts_center.capacity.legend.secondary",
              ),
              value: secondary,
            },
            {
              variant: "available",
              label: i18n.t(
                "js.email_accounts_center.capacity.legend.available",
              ),
              value: available,
            },
          ]}
        />
      )}
    </>
  )
}
